import { useQuery } from '@tanstack/react-query'
import {
  fetchGeneralDashboard,
  fetchSixDaysAgo,
  fetchWebTraffic,
} from '../../../gateways/breadstack/dashboardApis'
import type {
  GeneralDashboardResponse,
  RevenueValue,
  SixDaysAgoResponse,
  WebTrafficResponse,
} from '../../../gateways/breadstack/dashboardApis'

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

// online_revenue / pos_revenue may be a plain number or a { current, previous,
// percentage_increase } object. Coerce to the numeric value (or null if absent).
const numFromMetric = (v: RevenueValue | null | undefined): number | null => {
  if (v == null) return null
  if (typeof v === 'number') return v
  return v.current ?? null
}

// Extract the trend % from a metric object; null for plain numbers / missing.
const trendFromMetric = (v: RevenueValue | null | undefined): number | null => {
  if (v == null || typeof v === 'number') return null
  return v.percentage_increase ?? null
}

// revenue_by_day rows: [date, online_revenue, pos_revenue] → { date, online, inStore }
// Column order is resolved from headers (falling back to positions 1 & 2) so the
// series stay correct regardless of header naming or ordering.
const mapRevenueByDay = (data: GeneralDashboardResponse) => {
  const src = data.revenue_by_day
  if (!src?.rows?.length) return []
  const headers = (src.headers ?? []).map((h) => String(h).toLowerCase())
  const onlineIdx = headers.findIndex((h) => h.includes('online'))
  const posIdx = headers.findIndex((h) => h.includes('pos') || h.includes('store'))
  const oi = onlineIdx > 0 ? onlineIdx : 1
  const pi = posIdx > 0 ? posIdx : 2
  return src.rows.map((row) => ({
    date: String(row[0]),
    online: Number(row[oi]) || 0,
    inStore: Number(row[pi]) || 0,
  }))
}

// locations_revenue rows: [date, revenue] per location per day
// Flatten to { locationId, category (location name), date, value }
const mapLocationsRevenue = (data: GeneralDashboardResponse) =>
  (data.locations_revenue ?? []).flatMap((loc) =>
    (loc.rows ?? []).map(([date, value]) => ({
      locationId: String(loc.location_id),
      category: loc.location_name,
      date,
      value,
    })),
  )

// shipping_method_revenue_by_day rows: [date, customer_pickup_revenue, local_delivery_revenue]
// → { date, inStore (pickup), delivery }
const mapShippingRevenue = (data: GeneralDashboardResponse) =>
  (data.shipping_method_revenue_by_day?.rows ?? []).map(([date, pickup, delivery]) => ({
    date,
    inStore: pickup,
    delivery,
  }))

// number_of_orders_by_day rows: [date, number_of_online_orders, number_of_pos_orders]
// → { date, online, pos }
const mapOrdersByDay = (data: GeneralDashboardResponse) =>
  (data.number_of_orders_by_day?.rows ?? []).map(([date, online, pos]) => ({
    date,
    online,
    pos,
  }))

export const mapGeneralDashboard = (data: GeneralDashboardResponse) => ({
  revenue: data.revenue,
  cogs: data.cogs,
  profit: data.profit,
  numberOfOrders: data.number_of_orders,
  averageOrderAmount: data.average_order_amount,
  numberOfProductsSold: data.number_of_products_sold,
  averageCartSize: data.average_cart_size,
  discount: data.discount,
  averageDailyRevenue: data.average_daily_revenue,
  averageDailyOrders: data.average_daily_orders,
  customers: data.customers,
  topProducts: data.top_products ?? [],
  topCategories: data.top_categories ?? [],
  topBrands: data.top_brands ?? [],
  topBudtenders: data.top_budtenders ?? [],
  locationsPerformance: data.locations_performance ?? [],
  locationsRevenue: mapLocationsRevenue(data),
  shippingRevenue: mapShippingRevenue(data),
  ordersByDay: mapOrdersByDay(data),
  // Clean per-channel revenue from read_general (null when the field is absent)
  onlineRevenue: numFromMetric(data.online_revenue),
  posRevenue: numFromMetric(data.pos_revenue),
  onlineRevenueTrend: trendFromMetric(data.online_revenue),
  posRevenueTrend: trendFromMetric(data.pos_revenue),
  revenueByDay: mapRevenueByDay(data),
  // Conversion rate (percent 0..100) and total sessions (count) from read_general.
  // Optional — absent for non-pantry stores.
  conversionRate: data.conversion_rate,
  totalSessions: data.total_sessions,
})

export type MappedGeneralDashboard = ReturnType<typeof mapGeneralDashboard>

// six_days_ago: { headers: ["orders_count", "revenue", "profit", "new_customers"], data: number[][] }
// → { ordersCount: [{label, value}], revenue: [...], profit: [...], newCustomers: [...] }
export const mapSixDaysAgo = (raw: SixDaysAgoResponse) => {
  if (!raw?.data?.length || !raw?.headers?.length) {
    return { ordersCount: [], revenue: [], profit: [], newCustomers: [] }
  }
  return raw.headers.reduce<Record<string, { label: string; value: number }[]>>(
    (acc, rawKey, colIdx) => {
      const camelKey = rawKey.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
      acc[camelKey] = raw.data.map((row, rowIdx) => ({
        label: `Day ${rowIdx + 1}`,
        value: Number(row[colIdx]) || 0,
      }))
      return acc
    },
    {},
  ) as {
    ordersCount: { label: string; value: number }[]
    revenue: { label: string; value: number }[]
    profit: { label: string; value: number }[]
    newCustomers: { label: string; value: number }[]
  }
}

export type MappedSixDaysAgo = ReturnType<typeof mapSixDaysAgo>

// web_traffic: { headers: [...], data: string[][] }
// → array of channel objects with named keys
export const mapWebTraffic = (raw: WebTrafficResponse) => {
  if (!raw?.data?.length || !raw?.headers?.length) return []
  return raw.data.map((row) =>
    raw.headers.reduce<Record<string, string | number>>((obj, key, idx) => {
      obj[key] = row[idx]
      return obj
    }, {}),
  ) as {
    channelGrouping: string
    returningUsers: string
    newUsers: string
    sessions: string
    avgSessionDuration: string
    pageviewsPerSession: string
    avgTimeOnPage: string
    goalConversionRateAll: string
  }[]
}

export type MappedWebTraffic = ReturnType<typeof mapWebTraffic>

// ---------------------------------------------------------------------------
// React Query hooks
// ---------------------------------------------------------------------------

interface GeneralDashboardQueryParams {
  startDate: string
  endDate: string
  locationId?: string | null
  source?: string
  netRevenue?: boolean
  enabled?: boolean
}

export const useGeneralDashboardQuery = ({
  startDate,
  endDate,
  locationId,
  source = '',
  netRevenue,
  enabled = true,
}: GeneralDashboardQueryParams) =>
  useQuery({
    queryKey: ['dashboard:general', startDate, endDate, locationId, source, netRevenue],
    queryFn: () => fetchGeneralDashboard({ startDate, endDate, locationId, source, netRevenue }),
    select: mapGeneralDashboard,
    enabled: !!(startDate && endDate) && enabled,
  })

interface SixDaysAgoQueryParams {
  locationId?: string | null
  source?: string
  netRevenue?: boolean
  storeId?: string
  enabled?: boolean
}

export const useSixDaysAgoQuery = ({
  locationId,
  source = '',
  netRevenue,
  storeId,
  enabled = true,
}: SixDaysAgoQueryParams) =>
  useQuery({
    queryKey: ['dashboard:sixDaysAgo', storeId, locationId, source, netRevenue],
    queryFn: () => fetchSixDaysAgo({ locationId, source, netRevenue }),
    select: mapSixDaysAgo,
    enabled: !!storeId && enabled,
  })

interface WebTrafficQueryParams {
  startDate: string
  endDate: string
  enabled?: boolean
}

export const useWebTrafficQuery = ({ startDate, endDate, enabled = true }: WebTrafficQueryParams) =>
  useQuery({
    queryKey: ['dashboard:webTraffic', startDate, endDate],
    queryFn: () => fetchWebTraffic({ startDate, endDate }),
    select: mapWebTraffic,
    enabled: !!(startDate && endDate) && enabled,
  })
